import { useState } from 'react';

import formatPrice from 'utils/formatPrice';
import CartProducts from './CartProducts';

import { useCart } from 'contexts/cart-context';
import { useAuth } from 'contexts/auth-context';
import { useI18n } from 'contexts/i18n-context';
import { createRestaurantOrder } from 'services/orders';

import * as S from './style';

type CartStep = 'review' | 'checkout';

interface IProps {
  onOrderSuccess?(): void;
}

const Cart = ({ onOrderSuccess }: IProps) => {
  const { products, total, isOpen, openCart, closeCart, clearCart } = useCart();
  const { auth } = useAuth();
  const { t } = useI18n();
  const [cartStep, setCartStep] = useState<CartStep>('review');
  const [restaurantName, setRestaurantName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formattedTotal = `${total.currencyFormat} ${formatPrice(
    total.totalPrice,
    total.currencyId
  )}`;
  const subtotalText =
    total.productQuantity && !total.hasPricedProducts
      ? t('product.priceUnavailable')
      : formattedTotal;
  const isAuthenticatedCheckout = Boolean(auth?.accessToken);

  const handleCheckout = async () => {
    if (!total.productQuantity) {
      alert(t('cart.emptyAlert'));
      return;
    }

    const trimmedRestaurantName = restaurantName.trim();

    if (!isAuthenticatedCheckout && !trimmedRestaurantName) {
      alert(t('cart.restaurantNameRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      await createRestaurantOrder({
        ...(isAuthenticatedCheckout
          ? {}
          : {
              restaurantName: trimmedRestaurantName,
              phoneNumber: phoneNumber.trim(),
            }),
        note: orderNote.trim(),
        accessToken: auth?.accessToken,
        items: products.map((product) => ({
          product: product.id,
          quantity: product.quantity,
        })),
      });
      alert(t('cart.orderSuccess'));
      clearCart();
      setRestaurantName('');
      setPhoneNumber('');
      setOrderNote('');
      setCartStep('review');
      closeCart();
      onOrderSuccess?.();
    } catch (error) {
      alert(t('cart.orderFailure'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckoutStep = () => {
    if (!total.productQuantity) {
      alert(t('cart.emptyAlert'));
      return;
    }

    setCartStep('checkout');
  };

  const handleToggleCart = (isOpen: boolean) => () => {
    if (isOpen) {
      setCartStep('review');
      closeCart();
      return;
    }

    openCart();
  };

  return (
    <S.Container
      isOpen={isOpen}
      role={isOpen ? 'dialog' : undefined}
      aria-label={t('cart.shoppingCart')}
    >
      <S.CartButton
        onClick={handleToggleCart(isOpen)}
        aria-label={isOpen ? t('cart.close') : t('cart.open')}
      >
        {isOpen ? (
          <span aria-hidden="true">x</span>
        ) : (
          <S.CartIcon inverted>
            <S.CartQuantity title={t('cart.quantityTitle')}>
              {total.productQuantity}
            </S.CartQuantity>
          </S.CartIcon>
        )}
      </S.CartButton>

      {isOpen && (
        <S.CartContent>
          <S.CartContentHeader>
            <S.HeaderEyebrow>{t('brand.name')}</S.HeaderEyebrow>
            <S.HeaderRow>
              <S.CartIcon large>
                <S.CartQuantity>{total.productQuantity}</S.CartQuantity>
              </S.CartIcon>
              <S.HeaderTitle>{t('cart.title')}</S.HeaderTitle>
            </S.HeaderRow>
          </S.CartContentHeader>

          {cartStep === 'review' ? (
            <CartProducts products={products} />
          ) : (
            <S.CheckoutForm aria-label={t('cart.checkoutDetails')}>
              <S.StepBackButton
                type="button"
                onClick={() => setCartStep('review')}
              >
                {t('cart.backToCart')}
              </S.StepBackButton>
              <S.CheckoutTitle>{t('cart.checkoutDetails')}</S.CheckoutTitle>
              {!isAuthenticatedCheckout && (
                <>
                  <S.RestaurantField>
                    <S.RestaurantLabel htmlFor="restaurant-name">
                      {t('cart.restaurantName')}
                    </S.RestaurantLabel>
                    <S.RestaurantInput
                      id="restaurant-name"
                      name="restaurantName"
                      type="text"
                      value={restaurantName}
                      onChange={(event) => setRestaurantName(event.target.value)}
                      placeholder={t('cart.restaurantNamePlaceholder')}
                    />
                  </S.RestaurantField>
                  <S.RestaurantField>
                    <S.RestaurantLabel htmlFor="phone-number">
                      {t('cart.phoneNumber')}
                    </S.RestaurantLabel>
                    <S.RestaurantInput
                      id="phone-number"
                      name="phoneNumber"
                      type="tel"
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                      placeholder={t('cart.phoneNumberPlaceholder')}
                    />
                  </S.RestaurantField>
                </>
              )}
              <S.RestaurantField>
                <S.RestaurantLabel htmlFor="order-note">
                  {t('cart.orderNote')}
                </S.RestaurantLabel>
                <S.OrderNoteInput
                  id="order-note"
                  name="orderNote"
                  value={orderNote}
                  onChange={(event) => setOrderNote(event.target.value)}
                  placeholder={t('cart.orderNotePlaceholder')}
                />
              </S.RestaurantField>
            </S.CheckoutForm>
          )}

          <S.CartFooter aria-label={t('cart.subtotalLabel')}>
            <S.Sub>{t('cart.subtotal')}</S.Sub>
            <S.SubPrice>
              <S.SubPriceValue>{subtotalText}</S.SubPriceValue>
              <S.SubPriceInstallment>
                {total.installments ? (
                  <span>
                    {t('cart.installments', {
                      installments: total.installments,
                      amount: `${total.currencyFormat} ${formatPrice(
                        total.totalPrice / total.installments,
                        total.currencyId
                      )}`,
                    })}
                  </span>
                ) : null}
              </S.SubPriceInstallment>
            </S.SubPrice>
            <S.CheckoutButton
              onClick={
                cartStep === 'review' ? handleCheckoutStep : handleCheckout
              }
              disabled={isSubmitting}
            >
              {cartStep === 'review' ? t('cart.checkout') : t('cart.submitOrder')}
            </S.CheckoutButton>
          </S.CartFooter>
        </S.CartContent>
      )}
    </S.Container>
  );
};

export default Cart;
